import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { FaqBlockProps } from "./FaqBlock.types";
import { FaqBlockContent } from "@/modules/hub/features/ResourceLibrary/components/ResourceDetailsContentBlock/ResourceDetailsContentBlock";

export function FaqBlock({ content }: FaqBlockProps) {
  let faqData: FaqBlockContent | null = null;

  try {
    faqData = JSON.parse(content) as FaqBlockContent;
  } catch (e) {
    console.error("Failed to parse FAQ content:", e);
    return (
      <div className="my-6 p-4 border border-destructive/50 rounded-lg">
        <p className="text-destructive">Error: Invalid FAQ content format</p>
      </div>
    );
  }

  if (!faqData.items || faqData.items.length === 0) {
    return null;
  }

  return (
    <div className="my-6">
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold">Frequently Asked Questions</h3>
        </div>
        <Accordion className="w-full space-y-2" collapsible type="single">
          {faqData.items.map((item, index) => (
            <AccordionItem
              className="border border-gray-200 dark:border-gray-700 rounded-lg px-4 bg-white dark:bg-gray-900/50 data-[state=open]:bg-blue-50/50 dark:data-[state=open]:bg-blue-900/20 transition-colors"
              key={index}
              value={`item-${index}`}
            >
              <AccordionTrigger className="text-left hover:no-underline py-4 font-medium">
                <div className="flex items-start gap-3 text-left">
                  <span className="flex-shrink-0 text-sm font-semibold text-blue-600 dark:text-blue-400 mt-0.5">
                    Q{index + 1}.
                  </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {item.question}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-0 pb-4">
                <div className="pl-8 prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300">
                  <ReactMarkdown
                    components={{
                      // All links in resource content open in new tab
                      a: ({ node, children, href, ...props }) => (
                        <a
                          href={href}
                          rel="noopener noreferrer"
                          target="_blank"
                          {...props}
                        >
                          {children}
                        </a>
                      ),
                    }}
                    remarkPlugins={[remarkGfm]}
                  >
                    {item.answer}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
